import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { UrlBuilder } from '../../core';
import { GridHeader, GridDetails, ActionType, GridEvent } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { ActivityLogger } from '../../activities/activity-logger';
import { FormControl } from '@angular/forms';
import { SamplesInfo, RegisteredSampleInfo } from './interface/sample-info';
import { SampleDetailsService } from './service/sample-details-service';
import { Observable, merge, of, Subscription } from 'rxjs';
import { DateRangeFilterValue, RangeKind } from '../../shared/time-frame-filter/timeframe-filter.component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AccessionDetailsService } from '../register-samples/service/accession-details-service';
import { RegisterSampleStorage } from '../register-samples/register-sample-storage';
import * as moment from 'moment';

@Component({
    selector: 'registered-samples',
    template: require('./sample-details.component.html'),
    styles: [require('./sample-details.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisteredSamplesComponent implements OnInit {

    public label = 'Registered Samples';
    public gridDetails: GridDetails;
    public isLoadingCompleted = false;
    public defaultSortField = 'subjectId';
    public gridData: RegisteredSampleInfo[] = [];
    public registeredSamples: RegisteredSampleInfo[] = [];
    public searchTerm = new FormControl('');
    public showSubmitAlert: Observable<boolean>;
    public displayDateFilter = true;
    public selectedDateRange: DateRangeFilterValue;
    public searchSubscription: Subscription;
    public timeFrameRanges: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: false },
        { key: 'monthly', value: 'Last 30 days', selected: true },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];
    public activeFilter = false;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public defaultPageSize = 25;

    constructor(
        private readonly urlBuilder: UrlBuilder,
        private activityLogger: ActivityLogger,
        private samplesService: SampleDetailsService,
        private accessionService: AccessionDetailsService,
        private registerSampleStorage: RegisterSampleStorage,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.showSubmitAlert = this.samplesService.showSubmitAlert;
        setTimeout(() => { this.closeAlert(); this.changeDetectorRef.markForCheck(); }, 10000);
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
        this.searchSubscription = searchTermFlow.subscribe((term) => {
            this.applySearch(term);
            this.changeDetectorRef.markForCheck();
        });
        activityLogger.logActivity('Registered Samples - List View');

    }

    public onQuickFilterChange() {
        this.activeFilter = !this.activeFilter;
        this.gridData = this.activeFilter ? this.registeredSamples.filter(t => !t.isInShipment) : this.registeredSamples;
        this.gridDetails = { header: this.samplesService.getRegisteredSamplesHeader(), data: this.gridData };
        this.changeDetectorRef.markForCheck();
    }

    public ngOnInit() {
        this.timeFrameFilterChange(this.samplesService.getMonthlyRange());
    }

    public loadRegisteredSamples(dateBegin: string, dateEnd: string) {
        this.samplesService.getRegisteredSamplesList(dateBegin, dateEnd).subscribe((gridData: RegisteredSampleInfo[]) => {
            this.registeredSamples = gridData;
            this.gridData = this.activeFilter ? gridData.filter(t => !t.isInShipment) : gridData;
            this.gridDetails = { header: this.samplesService.getRegisteredSamplesHeader(), data: this.gridData };
            this.isLoadingCompleted = true;
            this.changeDetectorRef.markForCheck();
        });
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        if ($event === this.selectedDateRange && $event.rangeType !== 'dateRange') { return; }
        this.isLoadingCompleted = false;
        this.loadRegisteredSamples($event.dateBegin, $event.dateEnd);
        this.selectedDateRange = $event;
    }

    public applySearch(term: string) {
        let headerList = this.samplesService.getRegisteredSamplesHeader();
        if (!term || !term.length || !this.gridData.length) {
            this.gridDetails = { header: headerList, data: this.gridData };
        } else {
            let filterData = this.samplesService.applySearch(term, headerList, this.gridData);
            this.gridDetails = { header: headerList, data: filterData };
        }
        this.changeDetectorRef.markForCheck();
    }

    public registerSample() {
        this.urlBuilder.esa().sampleManagement().registerNewSample().navigate();
    }

    public closeAlert() {
        this.samplesService.showSubmitAlert = new Observable(ob => ob.next(false));
        this.showSubmitAlert = this.samplesService.showSubmitAlert;
    }

    public onGridItemClick($event: any) {
        if ($event.header.actionType === ActionType.none) {
            window.open(`${$event.data.projectNumber}/${$event.data.investigatorSiteNumber}/documents/lab-manuals/synopsis`);
        }
        if ($event.header.actionType === ActionType.view) {
            localStorage.setItem('resumeRegistration', $event.data.accessionNumber);
            this.urlBuilder.esa().sampleManagement().resumeRegistration('view').navigate();
        }
        if ($event.header.actionType === ActionType.delete) {
            this.OpenDeleteSampleRegistration($event.data.accessionNumber, $event.data.registeredVisitId, true);
        }
    }

    public onPagePerItemsChange($event: any) {
        this.defaultPageSize = $event.pageSize;    
    }

    private async OpenDeleteSampleRegistration(accessionNo: string, registedVisitId: number, isYesButtonClicked: boolean) {
        (await this.samplesService.OpenDeleteSampleRegistration(accessionNo, registedVisitId, isYesButtonClicked)).toPromise().then(result => {
            if (result) {
                this.loadRegisteredSamples(this.selectedDateRange.dateBegin, this.selectedDateRange.dateEnd);
                this.changeDetectorRef.markForCheck();
            }
        });
    }
}
